import React from 'react';
import { Booking } from '@/lib/types';
import { 
  CalendarCheck, 
  CalendarX, 
  Calendar,
  MessageSquare,
  Mail,
  User,
  Building,
  Clock
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';

interface BookingDetailsProps {
  booking: Booking;
}

export default function BookingDetails({ booking }: BookingDetailsProps) {
  // Helper function to get badge based on status
  const getStatusBadge = (status: Booking['status']) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'confirmed':
        return <Badge className="bg-green-500">Confirmed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return null;
    }
  };
  
  if (!booking) return null;
  
  return (
    <div className="grid gap-6 py-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Booking #{booking.id}</h3>
        {getStatusBadge(booking.status)}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium flex items-center mb-2">
              <User className="w-4 h-4 mr-2" />
              Client Information
            </h3>
            <div className="grid gap-2">
              <div className="flex">
                <span className="text-sm font-medium w-20">Name:</span>
                <span className="text-sm">{booking.name}</span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium w-20">Email:</span>
                <span className="text-sm">
                  <a href={`mailto:${booking.email}`} className="text-blue-600 hover:underline flex items-center">
                    <Mail className="w-3 h-3 mr-1" />
                    {booking.email}
                  </a>
                </span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium w-20">Business:</span>
                <span className="text-sm">{booking.business}</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div>
            <h3 className="text-sm font-medium flex items-center mb-2">
              <Calendar className="w-4 h-4 mr-2" />
              Appointment Details
            </h3>
            <div className="grid gap-2">
              <div className="flex">
                <span className="text-sm font-medium w-20">Service:</span>
                <span className="text-sm">{booking.service}</span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium w-20">Date:</span>
                <span className="text-sm flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  {format(new Date(booking.date), 'MMMM d, yyyy')}
                </span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium w-20">Time:</span>
                <span className="text-sm flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {booking.time}
                </span>
              </div>
              <div className="flex">
                <span className="text-sm font-medium w-20">Created:</span>
                <span className="text-sm">
                  {format(new Date(booking.createdAt), 'MMMM d, yyyy')}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          {booking.message && (
            <div>
              <h3 className="text-sm font-medium flex items-center mb-2">
                <MessageSquare className="w-4 h-4 mr-2" />
                Client Message
              </h3>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm whitespace-pre-wrap">{booking.message}</p>
              </div>
            </div>
          )}
          
          {booking.notes && (
            <div>
              <h3 className="text-sm font-medium flex items-center mb-2">
                <MessageSquare className="w-4 h-4 mr-2" />
                Admin Notes
              </h3>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm whitespace-pre-wrap">{booking.notes}</p>
              </div>
            </div>
          )}
          
          {booking.calendarEventLink && (
            <div>
              <h3 className="text-sm font-medium flex items-center mb-2">
                <Calendar className="w-4 h-4 mr-2" />
                Video Meeting
              </h3>
              <div className="bg-blue-50 p-3 rounded-md">
                <a 
                  href={booking.calendarEventLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm flex items-center"
                >
                  <CalendarCheck className="w-4 h-4 mr-1" />
                  Open Zoom Meeting Link
                </a>
              </div>
            </div>
          )}
          
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md mt-4">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">Zoom Meeting Instructions</h3>
            <p className="text-xs text-green-700 dark:text-green-400">
              This is a virtual appointment conducted via Zoom. You'll need to join the video call at the scheduled time. 
              The link will be provided in your confirmation email and also available in your calendar invitation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}